import type { Control, DeepMap, FieldError, FormState } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { OutlineIconButtonMedium } from "../../../../../components/Buttons/Buttons";
import { DeleteIcon } from "../../../../../components/Icons/Icons";
import { RadioButtonWithController } from "../../../../../components/RadioButton/RadioButton";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { spacingTokens } from "../../../../../designTokens";
import type { OptionType } from "../../../../../types/types";

export type QuantitativeFilterSlot =
  | "is_attribute"
  | "min_attribute"
  | "max_attribute";

/**
 * The form-state shape for one Quantitative Filter card. A single flat
 * shape rather than a `kind`-discriminated union — RHF v6's `useFieldArray`
 * doesn't cleanly support a per-row shape that changes when the Single/Range
 * radio flips, so every row always carries all four fields; which ones are
 * required is enforced by `quantitativeFilterRowSchema`'s `superRefine`
 * (see ConfigureAdvancedFilterForm.tsx), and the submit handler projects
 * only the fields relevant to `kind` onto the wire payload.
 */
export type QuantitativeFilterFormRow = {
  kind: "single" | "range";
  display_name: string;
  is_attribute: OptionType | null;
  min_attribute: OptionType | null;
  max_attribute: OptionType | null;
};

export const emptyQuantitativeFilterRow: QuantitativeFilterFormRow = {
  kind: "single",
  display_name: "",
  is_attribute: null,
  min_attribute: null,
  max_attribute: null,
};

const Card = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_xxs")};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
`;

const RadioRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
`;

export const QuantitativeFilterCard = ({
  index,
  control,
  register,
  errors,
  formState,
  kind,
  defaultValues,
  onKindChange,
  buildOptionsForSlot,
  disabled,
  onRemove,
}: {
  index: number;
  control: Control<any>;
  register: (validationRules?: Record<string, unknown>) => any;
  errors: DeepMap<Record<string, any>, FieldError>;
  formState: FormState<Record<string, any>>;
  kind: "single" | "range";
  defaultValues: QuantitativeFilterFormRow;
  onKindChange: (kind: "single" | "range") => void;
  /** Options for one of the three attribute slots, already filtered to numeric-typed columns of the selected collection minus every attribute used elsewhere in this form. */
  buildOptionsForSlot: (slot: QuantitativeFilterSlot) => OptionType[];
  disabled: boolean;
  onRemove: () => void;
}) => {
  const { t } = useTranslation();
  const name = `quantitative_filters[${index}]`;
  const hasDisplayNameError = !!errors?.[`${name}.display_name`];
  const hasIsAttributeError = !!errors?.[`${name}.is_attribute`];
  const hasMinAttributeError = !!errors?.[`${name}.min_attribute`];

  return (
    <Card>
      <CardHeader>
        <Controller
          control={control}
          name={`${name}.kind`}
          defaultValue={defaultValues.kind}
          render={({ onChange, value, ...props }) => (
            <RadioRow>
              <RadioButtonWithController
                {...props}
                value="single"
                checked={value === "single"}
                optionTitle={t("Single value")}
                onChange={() => {
                  onChange("single");
                  onKindChange("single");
                }}
                disabled={disabled}
              />
              <RadioButtonWithController
                name={props.name}
                value="range"
                checked={value === "range"}
                optionTitle={t("Range of values")}
                onChange={() => {
                  onChange("range");
                  onKindChange("range");
                }}
                disabled={disabled}
              />
            </RadioRow>
          )}
        />
        <OutlineIconButtonMedium
          testid={`delete-quantitative-filter-${index}`}
          onClick={onRemove}
          type="button"
        >
          <DeleteIcon />
        </OutlineIconButtonMedium>
      </CardHeader>

      <div
        hidden={kind !== "range"}
        style={{
          marginBottom: hasDisplayNameError
            ? spacingTokens.spacing_lg
            : spacingTokens.spacing_md,
        }}
      >
        <TextField
          name={`${name}.display_name`}
          label={t("Display Name")}
          theref={register()}
          defaultValue={defaultValues.display_name}
          formState={formState}
          errors={errors}
          type="text"
        />
      </div>

      <div
        style={{
          marginBottom: hasIsAttributeError
            ? spacingTokens.spacing_lg
            : spacingTokens.spacing_md,
        }}
      >
        <Controller
          as={SelectBoxV2}
          control={control}
          name={`${name}.is_attribute`}
          defaultValue={defaultValues.is_attribute}
          placeholder={
            kind === "single" ? t("Select Attribute") : t("Is attribute")
          }
          options={buildOptionsForSlot("is_attribute")}
          disabled={disabled}
          errors={errors}
          formState={formState}
        />
      </div>

      <div hidden={kind !== "range"}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: hasMinAttributeError
              ? spacingTokens.spacing_lg
              : spacingTokens.spacing_md,
          }}
        >
          <Controller
            as={SelectBoxV2}
            control={control}
            name={`${name}.min_attribute`}
            defaultValue={defaultValues.min_attribute}
            placeholder={t("Min attribute")}
            options={buildOptionsForSlot("min_attribute")}
            disabled={disabled}
            errors={errors}
            formState={formState}
          />
          <Controller
            as={SelectBoxV2}
            control={control}
            name={`${name}.max_attribute`}
            defaultValue={defaultValues.max_attribute}
            placeholder={t("Max attribute")}
            options={buildOptionsForSlot("max_attribute")}
            disabled={disabled}
            errors={errors}
            formState={formState}
          />
        </div>
      </div>
    </Card>
  );
};
